const createTextNode = (text, marks) => {
  const node = { type: "text", text };

  if (marks?.length) {
    node.marks = marks;
  }

  return node;
};

const createParagraphNode = (value, allowMarkdownBold = false) => {
  const text = String(value || "");

  if (!text) {
    return { type: "paragraph" };
  }

  if (!allowMarkdownBold) {
    return { type: "paragraph", content: [createTextNode(text)] };
  }

  const content = [];
  const boldPattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match = boldPattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      content.push(createTextNode(text.slice(lastIndex, match.index)));
    }

    content.push(createTextNode(match[1], [{ type: "bold" }]));
    lastIndex = match.index + match[0].length;
    match = boldPattern.exec(text);
  }

  if (lastIndex < text.length) {
    content.push(createTextNode(text.slice(lastIndex)));
  }

  return content.length
    ? { type: "paragraph", content }
    : { type: "paragraph" };
};

const buildTableContentFromRows = (
  rows,
  withHeaderRow = false,
  allowMarkdownBold = false,
) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  const maxCols = Math.max(...rows.map((row) => row.length), 0);
  if (maxCols === 0) {
    return null;
  }

  const normalizedRows = rows.map((row) => [
    ...row,
    ...Array(Math.max(0, maxCols - row.length)).fill(""),
  ]);

  return {
    type: "table",
    content: normalizedRows.map((row, rowIndex) => ({
      type: "tableRow",
      content: row.map((cell) => ({
        type: withHeaderRow && rowIndex === 0 ? "tableHeader" : "tableCell",
        content: [createParagraphNode(cell, allowMarkdownBold)],
      })),
    })),
  };
};

const getDirectTableCells = (rowElement) =>
  Array.from(rowElement.children).filter((child) =>
    ["TH", "TD"].includes(child.tagName),
  );

const splitMarkdownTableRow = (line) => {
  let trimmedLine = String(line || "").trim();

  if (trimmedLine.startsWith("|")) {
    trimmedLine = trimmedLine.slice(1);
  }

  if (trimmedLine.endsWith("|")) {
    trimmedLine = trimmedLine.slice(0, -1);
  }

  const cells = [];
  let currentCell = "";

  for (let index = 0; index < trimmedLine.length; index += 1) {
    const char = trimmedLine[index];

    if (char === "|" && trimmedLine[index - 1] !== "\\") {
      cells.push(currentCell.trim().replace(/\\\|/g, "|"));
      currentCell = "";
    } else {
      currentCell += char;
    }
  }

  cells.push(currentCell.trim().replace(/\\\|/g, "|"));
  return cells;
};

const isMarkdownSeparatorRow = (row) =>
  row.length > 0 && row.every((cell) => /^:?-+:?$/.test(cell.trim()));

export const convertMarkdownTableToTableContent = (plainText) => {
  if (!plainText || !plainText.includes("|")) {
    return null;
  }

  const logicalRows = plainText
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && line.includes("|"));

  if (logicalRows.length < 2) {
    return null;
  }

  const parsedRows = logicalRows.map(splitMarkdownTableRow);
  if (!isMarkdownSeparatorRow(parsedRows[1])) {
    return null;
  }

  const rows = [parsedRows[0], ...parsedRows.slice(2)];
  if (rows.length === 0) {
    return null;
  }

  return buildTableContentFromRows(rows, true, true);
};

export const convertClipboardHtmlToTableContent = (html) => {
  if (!html || !/<table[\s>]/i.test(html)) {
    return null;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const table = doc.querySelector("table");

    if (!table) {
      return null;
    }

    const trElements = Array.from(table.querySelectorAll("tr"));
    if (trElements.length === 0) {
      return null;
    }

    const grid = [];

    trElements.forEach((tr, rowIndex) => {
      if (!grid[rowIndex]) {
        grid[rowIndex] = [];
      }

      const cells = getDirectTableCells(tr);
      let columnIndex = 0;

      cells.forEach((cell) => {
        while (grid[rowIndex][columnIndex] !== undefined) {
          columnIndex += 1;
        }

        const text = String(cell.textContent || "")
          .replace(/\u00a0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const colspan =
          Number.parseInt(cell.getAttribute("colspan") || "1", 10) || 1;
        const rowspan =
          Number.parseInt(cell.getAttribute("rowspan") || "1", 10) || 1;

        for (let rowOffset = 0; rowOffset < rowspan; rowOffset += 1) {
          const targetRow = rowIndex + rowOffset;

          if (!grid[targetRow]) {
            grid[targetRow] = [];
          }

          for (let colOffset = 0; colOffset < colspan; colOffset += 1) {
            const targetColumn = columnIndex + colOffset;

            grid[targetRow][targetColumn] =
              rowOffset === 0 && colOffset === 0 ? text : "";
          }
        }

        columnIndex += colspan;
      });
    });

    const maxCols = Math.max(...grid.map((row) => row.length), 0);
    const rows = grid.map((row) =>
      Array.from({ length: maxCols }, (_, index) =>
        row[index] === undefined ? "" : row[index],
      ),
    );

    if (!rows.length || maxCols < 2) {
      return null;
    }

    const hasHeaderRow = !!trElements[0]?.querySelector("th");
    return buildTableContentFromRows(rows, hasHeaderRow);
  } catch (error) {
    console.error("Unable to parse HTML clipboard table", error);
    return null;
  }
};

export const convertTabularTextToTableContent = (plainText) => {
  if (!plainText || !plainText.includes("\t")) {
    return null;
  }

  const normalized = String(plainText)
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ");

  const lines = normalized.split("\n");

  while (lines.length > 0 && lines[lines.length - 1].trim() === "") {
    lines.pop();
  }

  if (lines.length === 0) {
    return null;
  }

  const rows = lines.map((line) =>
    line.split("\t").map((cell) =>
      String(cell)
        .replace(/\u00a0/g, " ")
        .trim(),
    ),
  );

  const maxCols = Math.max(...rows.map((row) => row.length), 0);
  if (maxCols < 2) {
    return null;
  }

  const normalizedRows = rows.map((row) => [
    ...row,
    ...Array(maxCols - row.length).fill(""),
  ]);

  return buildTableContentFromRows(normalizedRows, false, false);
};

const parseDelimitedLine = (line, delimiter) => {
  const cells = [];
  let currentCell = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"' && nextChar === '"') {
      currentCell += '"';
      index += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === delimiter && !inQuotes) {
      cells.push(currentCell.trim());
      currentCell = "";
    } else {
      currentCell += char;
    }
  }

  cells.push(currentCell.trim());
  return cells;
};

export const convertDelimitedTextToTableContent = (plainText) => {
  if (!plainText) {
    return null;
  }

  const lines = plainText
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim().length > 0);

  if (lines.length < 2 || lines.some((line) => line.includes("\t"))) {
    return null;
  }

  const delimiter = [",", ";"].find((candidate) =>
    lines.every((line) => parseDelimitedLine(line, candidate).length > 1),
  );

  if (!delimiter) {
    return null;
  }

  const rows = lines.map((line) => parseDelimitedLine(line, delimiter));
  const maxCols = Math.max(...rows.map((row) => row.length), 0);
  const multiColumnRows = rows.filter((row) => row.length === maxCols);

  if (maxCols < 2 || multiColumnRows.length < 2) {
    return null;
  }

  return buildTableContentFromRows(rows, false);
};

export const convertClipboardToTableContent = ({ html = "", plainText = "" }) => {
  if (plainText.includes("\t")) {
    const tabular = convertTabularTextToTableContent(plainText);

    if (tabular) {
      return tabular;
    }
  }

  const markdown = convertMarkdownTableToTableContent(plainText);

  if (markdown) {
    return markdown;
  }

  const htmlTable = convertClipboardHtmlToTableContent(html);

  if (htmlTable) {
    return htmlTable;
  }

  return convertDelimitedTextToTableContent(plainText);
};
